import React, { ReactNode, useState } from 'react';
import type { Column } from 'react-table';
import { Button, Table } from '@ver-uds/react';
import { Icon } from '@ver-uds/uswds-react';
import { CSVLink } from 'react-csv';
import FullWidthLayout from '../../../layouts/FullWidthLayout';
import HelpModal from '../../HelpModal';
import HelpModalContent from '../../../constants/HelpModalContent';

const CSVGuidelines = (): JSX.Element => {
  const pageTitle = 'E-Verify Enrollment';
  const csvTemplateData = 'Street Address,Suite/Other,City,State,Zip Code';
  const [stateHelpModalIsOpen, setStateHelpModalIsOpen] = useState(false);

  const StateHelpModal = (): JSX.Element => {
    return (
      <Button
        variant="tertiary"
        type="button"
        onClick={(): void => setStateHelpModalIsOpen(true)}
        style={{ marginTop: '0' }}
        className="margin-left-05"
      >
        <span className="usa-sr-only">Read more about State or U.S. Territory in the help modal</span>
        <Icon name="help" />
      </Button>
    );
  };

  type CSVTableData = {
    columnName: ReactNode;
    dataRequirements: string;
    formattingInstructions: string;
  };

  const csvTableColumns: Column<CSVTableData>[] = [
    {
      Header: 'Column Name',
      accessor: 'columnName',
      width: '120',
    },
    {
      Header: 'Data Requirements',
      accessor: 'dataRequirements',
      width: '120',
    },
    {
      Header: 'Formatting Instructions',
      accessor: 'formattingInstructions',
      width: '300',
    },
  ];

  const csvTableData: CSVTableData[] = [
    {
      columnName: 'Street Address',
      dataRequirements: 'Required',
      formattingInstructions: 'Street Address can be used with dashes, periods, and spaces only.',
    },
    {
      columnName: 'Suite/Other',
      dataRequirements: 'Optional',
      formattingInstructions: 'Suite/Other can be used with dashes, periods, and spaces only.',
    },
    { columnName: 'City', dataRequirements: 'Required', formattingInstructions: 'City is required.' },
    {
      columnName: (
        <>
          State <StateHelpModal />
        </>
      ),
      dataRequirements: 'Required',
      formattingInstructions: 'State must be a two-letter abbreviation.',
    },
    {
      columnName: 'Zipcode',
      dataRequirements: 'Required',
      formattingInstructions: 'Zip Code must include 5 digits, with numbers only.',
    },
  ];

  return (
    <>
      <FullWidthLayout title={`${pageTitle} Bulk Upload CSV Guidelines`} headerTitle={pageTitle}>
        <section className="padding-top-4 padding-bottom-8 grid-col-12 desktop:grid-col-9">
          <h1>Hiring Sites CSV Bulk Upload Guide</h1>
          <p className="usa-intro">
            This guide will help you prepare a spreadsheet to bulk upload large amounts of hiring site data into
            E-Verify.
          </p>

          <h2 className="margin-top-3">Things to keep in mind</h2>

          <p>
            One of the most common CSV import errors is file size. That can be caused by too many rows, columns, fields,
            or records in the file. If an upload fails because of file size issues, you need to break the file up into
            smaller files to enable a successful upload.
          </p>
          <ul>
            <li>CSV file must not exceed 1,000 rows</li>
            <li>CSV file size must not exceed 1 MB</li>
          </ul>

          <p>
            All column headers in your file(s) must match the{' '}
            <CSVLink
              data={csvTemplateData}
              filename="Hiring Sites Template"
              target="_blank"
              aria-label="Download Hiring Sites Template"
            >
              Hiring Site template
            </CSVLink>
            . If they don’t match exactly, the upload will fail. Note, an error alert will identify and export rows with
            missing or invalid field data.
          </p>
          <ul>
            <li>CSV file must meet column header requirements</li>
            <li>CSV file must meet column order requirements</li>
            <li>CSV file must include data in the required columns</li>
          </ul>

          <h2 className="margin-top-3">Setting up your CSV</h2>

          <p>
            Hiring site data must appear on one spreadsheet. Include all columns below in your CSV file, even if you
            don’t have all the information listed. Any optional fields that you don’t plan on filling out must be left
            blank — make sure <b>not</b> to type in “N/A” or anything else into the cell.
          </p>
          <Table columns={csvTableColumns} data={csvTableData} id="csv-guidelines-table" variant="default" />
          <HelpModal
            modalContent={HelpModalContent.state}
            open={stateHelpModalIsOpen}
            close={(): void => {
              setStateHelpModalIsOpen(false);
            }}
          />
        </section>
      </FullWidthLayout>
    </>
  );
};

export default CSVGuidelines;
